import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { ROW_NUMBER_COLUMN } from '@/grid/defaults';
import type { ColDef } from '@/grid/types';
import {
  dateOnlyFormatter,
  dateOnlyParser,
} from '@/grid/value-formatters/dateOnlyFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { AgllcBulkDonorScheduleInput } from '../utils/schema';

export function useAgllcColumns() {
  const { t } = useTranslation();
  const fixBulkDonorScheduleDateFormat = useFeatureFlag(
    'fix_bulk_donor_schedule_date_format',
  );

  return useMemo<ColDef<AgllcBulkDonorScheduleInput>[]>(
    () => [
      ROW_NUMBER_COLUMN,
      {
        headerName: t('bulkDonorScheduling.Priority', 'Priority'),
        field: 'priority',
        initialWidth: 70,
        editable: false,
      },
      {
        headerName: t('bulkDonorScheduling.Donor_ID', 'Donor ID'),
        field: 'donorNameToMatch',
        initialWidth: 90,
      },
      {
        headerName: t('bulkDonorScheduling.Pen', 'PEN'),
        field: 'pen',
        initialWidth: 60,
      },
      {
        headerName: t('bulkDonorScheduling.Registration', 'Registration'),
        field: 'donorRegistrationNumber',
        initialWidth: 140,
      },
      {
        headerName: t('bulkDonorScheduling.Breed', 'Breed'),
        field: 'donorBreed',
        initialWidth: 60,
      },
      {
        headerName: t('bulkDonorScheduling.Birth_Date', 'Birth Date'),
        field: 'birthDate',
        initialWidth: 100,
        valueFormatter: fixBulkDonorScheduleDateFormat
          ? dateOnlyFormatter
          : undefined,
        valueParser: fixBulkDonorScheduleDateFormat
          ? dateOnlyParser
          : undefined,
        cellEditorParams: fixBulkDonorScheduleDateFormat
          ? { useFormatter: true }
          : undefined,
      },
      {
        headerName: t('bulkDonorScheduling.Psi', 'PSI'),
        field: 'psi',
        initialWidth: 60,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          )
            return null;
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Gnm', 'GNM$'),
        field: 'gnm',
        initialWidth: 60,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          )
            return null;
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Gcm', 'GCM$'),
        field: 'gcm',
        initialWidth: 60,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          )
            return null;
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Pregnant', 'Pregnant'),
        field: 'pregnant',
        initialWidth: 80,
      },
      {
        headerName: t('bulkDonorScheduling.Program_Name', 'Program'),
        field: 'programName',
        initialWidth: 90,
      },
      {
        headerName: t('bulkDonorScheduling.Opu_Date', 'OPU Date'),
        field: 'opuDate',
        initialWidth: 100,
        valueFormatter: fixBulkDonorScheduleDateFormat
          ? dateOnlyFormatter
          : undefined,
        valueParser: fixBulkDonorScheduleDateFormat
          ? dateOnlyParser
          : undefined,
        cellEditorParams: fixBulkDonorScheduleDateFormat
          ? { useFormatter: true }
          : undefined,
      },
      {
        headerName: t('bulkDonorScheduling.OpuSite', 'OPU Site'),
        field: 'opuSite',
        initialWidth: 80,
      },
      {
        headerName: t('bulkDonorScheduling.Sire', 'Sire'),
        field: 'sire',
        initialWidth: 130,
      },
      {
        headerName: t('bulkDonorScheduling.NAAB', 'NAAB'),
        field: 'naab',
        initialWidth: 100,
      },
      {
        headerName: t('bulkDonorScheduling.Sir1Psi', 'SIR1 PSI'),
        field: 'sir1Psi',
        initialWidth: 70,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          )
            return null;
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Semen_Type', 'Semen Type'),
        field: 'semenType',
        initialWidth: 100,
      },
      {
        headerName: t('bulkDonorScheduling.Collection_Code', 'Collection Code'),
        field: 'collectionCode',
        initialWidth: 120,
      },
      {
        headerName: t('bulkDonorScheduling.BackupSire', 'Backup Sire'),
        field: 'backupSire',
        initialWidth: 120,
      },
      {
        headerName: t('bulkDonorScheduling.BackupNaab', 'Backup NAAB'),
        field: 'backupNaab',
        initialWidth: 100,
      },
      {
        headerName: t('bulkDonorScheduling.Sir2Psi', 'SIR2 PSI'),
        field: 'sir2Psi',
        initialWidth: 70,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          )
            return null;
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t(
          'bulkDonorScheduling.BackupSemenType',
          'Backup Semen Type',
        ),
        field: 'backupSemenType',
        initialWidth: 120,
      },
    ],
    [fixBulkDonorScheduleDateFormat, t],
  );
}
